import { IObservableArray, makeAutoObservable, observable } from 'mobx'
import { createContext, FC, useContext, useMemo } from 'react'
import { ItemId, TreeData, TreeItem } from 'tree-dnd'
import { TreeDataItem, TreeItemType } from '../types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

const isTmpId = (id: any) => id < 0

export class FilesTreeStore {
  rootId: 0 = 0
  items = observable.map<ItemId, TreeItem>({})
  draggingId: ItemId | null = null

  constructor() {
    makeAutoObservable(this)
  }

  setTree = (tree: TreeData) => {
    Object.entries(tree.items).forEach(([id, item]) => this.items.set(id, item))
  }

  get tree(): TreeData {
    return {
      rootId: this.rootId,
      items: Object.fromEntries(this.items),
    }
  }

  getItemParent = (itemId: ItemId) => {
    const item = this.items.get(itemId)
    if (!item) return

    if (!item.data?.parent_id) {
      return this.items.get(this.rootId.toString())
    }

    return this.items.get(item.data.parent_id)
  }

  isLast = (itemId: ItemId) => {
    if (!this.tree) return false

    const item = this.items.get(itemId)
    if (!item) return false

    const parent = this.getItemParent(item.id.toString())
    if (!parent || parent.children.length < 1) return false

    const lastChild = parent.children[parent.children.length - 1]
    return !!lastChild && lastChild.toString() === itemId
  }

  removeItem = (itemId: ItemId) => {
    if (!this.tree) return

    const item = this.items.get(itemId)

    if (item) {
      const parent = this.getItemParent(item.id.toString())
      if (!parent) return
      this.items.delete(itemId)
      parent.children = parent.children.filter((childId) => childId.toString() !== itemId)
      parent.hasChildren = parent.children.length > 0
    }
  }

  updateItem = (itemId: ItemId, data: TreeDataItem) => {
    if (!this.tree) return

    const item = this.items.get(itemId)
    if (!item) return

    if (isTmpId(itemId)) {
      const parent = this.getItemParent(item.id)
      if (!parent) return

      item.data = data
      this.items.delete(itemId)
      parent.children.unshift(data.id)
      ;(parent.children as IObservableArray<ItemId>).remove(itemId)
      this.items.set(data.id, {
        id: data.id,
        children: [],
        hasChildren: false,
        isExpanded: false,
        data: data,
      })
    } else {
      this.items.set(itemId, {
        ...item,
        data: data,
      })
    }
  }

  setItemType = (itemId: ItemId, type: TreeItemType) => {
    if (!this.tree) return

    const item = this.items.get(itemId)
    if (!item) return

    item.data.type = type
  }

  get isEmpty() {
    return !this.tree || this.items.size <= 1
  }

  get isLoaded() {
    return !this.isEmpty
  }
}

export const FilesTreeContext = createContext<FilesTreeStore | null>(null)

export const useFilesTreeStore = () => {
  const store = useContext(FilesTreeContext)

  if (!store) {
    throw new Error('FilesTreeContext should be invoked within a FilesTreeContext.Provider')
  }

  return store
}

export const FilesTreeProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const workspaceId = useCurrentWorkspaceId()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const store = useMemo(() => new FilesTreeStore(), [workspaceId])

  return <FilesTreeContext.Provider value={store}>{children}</FilesTreeContext.Provider>
}
